import {zod} from "@lya-protect/lya-protect-form-library/dist/Exports";

import "./birthDate.scss";
import {Form, InputButtonGroup, InputDate, useFormConfig,} from "@lya-protect/lya-protect-form-library/dist/FormAPI";
import {
  Button,
  Emphasis,
  FormAction,
  FormField,
  FormGrid,
  FormNotice,
  FormRow,
  FormTitle,
} from "@lya-protect/lya-protect-form-library/dist/UI";
import {InfoBloc} from "@src/components/infoBloc/InfoBloc";
import {InfoBulle} from "@src/components/infoBulle/InfoBulle";
import {InfoBulleButton} from "@src/components/infoBulleButton/InfoBulleButton";
import {NavButton} from "@src/components/navButton/NavButton";
import {PopperButton} from "@src/PopperButton";
import {OuiNonOptions} from "@src/utils";
import {getYear} from "date-fns";
import {StepProps} from "../../step/Steps";

export type BirthDateData = {
  birthDate?: Date;
};
type BirthDateProps = StepProps<BirthDateData>;

export const ERROR_BIRTHDATE =
  "La souscription du contrat Paxivie n’est pas ouverte aux personnes âgées de \
  plus de 75 ans au moment de la date de prise d'effet du contrat. L'âge \
  atteint est calculé par différence de millésimes entre l'année en cours \
  et l'année de naissance.";

const ERROR_PROTECTION =
  "Attention : la souscription pour les personnes soumises à une mesure de \
  protection juridique est uniquement disponible en contactant nos conseillers. \
  Vous pouvez les joindre par téléphone du lundi au vendredi de 9h à 12h30 et \
  de 13h30 à 17h au : 0 969 398 399 (appel non surtaxé)";

export const validBirth = (val: Date) =>
  getYear(new Date()) - getYear(val) <= 75;

const schema = zod.object({
  birthDate: zod
    .date({required_error: "Champ requis"})
    .refine(validBirth, ERROR_BIRTHDATE),
  underJuridicProtection: zod.boolean({required_error: "Champ requis"}),
});

type FormValues = zod.infer<typeof schema>;

export function BirthDate({changeStep, data, setData}: BirthDateProps) {
  const formConfig = useFormConfig<typeof schema, FormValues>({
    schema,
    defaultValues: data,
  });
  const birthDate = formConfig.watch("birthDate");
  const underJuridicProtection = formConfig.watch("underJuridicProtection");

  return (
    <div className="birthdate-step">
      <div className="stepper__centered-container">
        <div className="stepper__centered-content step__grid step__grid--padding-bottom">
          <NavButton onClick={() => changeStep("SITUATION", "backward")}>
            Précédent
          </NavButton>
          <Form
            className="situation-step__form"
            formConfig={formConfig}
            onSubmit={(submittedData) => {
              const normalizedData = {
                ...submittedData,
                birthDate: new Date(
                  submittedData.birthDate.getFullYear(),
                  submittedData.birthDate.getMonth(),
                  submittedData.birthDate.getDate(),
                  12,
                  0,
                  0
                ),
              };
              setData(normalizedData);
              changeStep("ACTIVITIES", "forward");
            }}
          >
            <FormGrid size="large">
              <FormTitle>
                <h1>
                  Avant de poursuivre, vérifions votre{" "}
                  <Emphasis>éligibilité</Emphasis> au contrat Paxivie :
                </h1>
              </FormTitle>
              <FormNotice>
                <Emphasis>*</Emphasis> Champs obligatoires
              </FormNotice>
              <FormRow>
                <FormField className="birthdate-step__input">
                  <InputDate
                    variant="large"
                    name="birthDate"
                    label={
                      <>
                        Quelle est votre{" "}
                        <Emphasis>date de naissance ? *</Emphasis>
                      </>
                    }
                  />
                </FormField>
              </FormRow>
              {validBirth(birthDate) && (
                <FormRow>
                  <FormField>
                    <InputButtonGroup
                      name="underJuridicProtection"
                      size="large"
                      className="birthdate-step__tst"
                      options={OuiNonOptions}
                      label={
                        <>
                          Faites-vous l&apos;objet d&apos;une mesure de
                          protection juridique ?
                          <PopperButton
                            popperPlacement="top"
                            openOnHover
                            withArrow
                            popperClassName="birthdate-step__popover"
                            renderButton={(props) => (
                              <InfoBulleButton {...props} />
                            )}
                            renderPopperContent={({setVisible}) => (
                              <InfoBulle
                                className="birthdate-step__infobulle"
                                onCancel={() => setVisible(false)}
                              >
                                Les différentes mesures de protection juridique
                                sont les suivantes : sauvegarde de justice,
                                curatelle simple, curatelle renforcée, tutelle
                                partielle, tutelle complète et éventuelles
                                habilitations.
                              </InfoBulle>
                            )}
                          />
                        </>
                      }
                    />

                    {underJuridicProtection && (
                      <InfoBloc
                        className="birthdate-step__warning"
                        variant="accent"
                      >
                        {ERROR_PROTECTION}
                      </InfoBloc>
                    )}
                  </FormField>
                </FormRow>
              )}
              <FormAction>
                {!underJuridicProtection && (
                  <Button
                    type="submit"
                    disabled={
                      formConfig.formState.isSubmitting ||
                      formConfig.formState.isSubmitSuccessful ||
                      underJuridicProtection
                    }
                  >
                    Suivant
                  </Button>
                )}
              </FormAction>
            </FormGrid>
          </Form>
        </div>
      </div>
    </div>
  );
}
